import { PauseCircleFilled, PlayCircleFilled } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { useEffect, useMemo, useRef, useState } from "react"
import { cn } from "styles/cn"

type AudioMessageProps = {
	theme?: "dark" | "light"
	src: string
}

export function AudioMessage({ src, theme = "dark" }: AudioMessageProps) {
	const [isPlaying, setIsPlaying] = useState(false)
	const [duration, setDuration] = useState(0)
	const [currentTime, setCurrentTime] = useState(0)
	const audioRef = useRef<HTMLAudioElement | null>(null)
	const Icon = isPlaying ? PauseCircleFilled : PlayCircleFilled
	const heights = useMemo(() => {
		const numberOfBarsToDisplay = 26
		const maxBarHeightPx = 22
		const minBarHeightPx = 10
		const baseHeightPx = 15
		const newHeights = []
		let lastHeight = Math.random() * baseHeightPx + 5
		for (let i = 0; i < numberOfBarsToDisplay; i++) {
			const variation = (Math.random() - 0.5) * minBarHeightPx
			lastHeight = Math.max(
				minBarHeightPx,
				Math.min(maxBarHeightPx, lastHeight + variation),
			)
			newHeights.push(lastHeight)
		}

		return newHeights
	}, [])

	const togglePlayPause = () => {
		if (isPlaying) {
			audioRef.current?.pause()
			setIsPlaying(false)
		} else {
			audioRef.current?.play()
			setIsPlaying(true)
		}
	}

	useEffect(() => {
		if (!isPlaying) {
			return
		}
		const updateTime = () => {
			if (currentTime >= duration) {
				setIsPlaying(false)
			} else {
				requestAnimationFrame(updateTime)
			}
		}
		updateTime()
	}, [isPlaying, currentTime, duration])

	return (
		<div className="flex items-center gap-2">
			<Button
				type="link"
				icon={<Icon aria-hidden />}
				className={cn("text-navy-100", theme === "light" && "text-white")}
				aria-label="Play voicemail message"
				onClick={togglePlayPause}
			/>
			<div className="relative h-5 w-25 overflow-hidden">
				{heights.map((height, index) => {
					const isActive = currentTime / duration > index / heights.length
					return (
						<div
							key={index}
							style={{ height: `${height}px`, left: `${index * 4}px` }}
							className={cn(
								"absolute bottom-1/2 w-0.5 translate-y-1/2 bg-navy-40 transition-colors",
								theme === "light" && "bg-white",
								isActive && "bg-navy-100",
							)}
						/>
					)
				})}
			</div>
			<span
				className={cn(
					"text-sm text-navy-60",
					theme === "light" && "text-white",
				)}
			>
				{formatTime(duration - currentTime)}
			</span>
			<audio
				ref={audioRef}
				src={src}
				onTimeUpdate={(event) =>
					setCurrentTime(event.currentTarget.currentTime)
				}
				onLoadedMetadata={(event) => {
					if (!isFinite(event.currentTarget.duration)) {
						return
					}
					setDuration(event.currentTarget.duration)
				}}
			/>
		</div>
	)
}

const formatTime = (time: number) => {
	const minutes = Math.floor(time / 60)
	const seconds = Math.floor(time % 60)
	return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
}
